import {
  ChevronLeftIcon,
  ChevronRightIcon,
  KeyIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';

import { parseSubagentTopic } from '@/sections/insights/aiAssistant/utils/subagentTopic.util';
import type { Conversation, Interaction } from '@/types';

import { InteractionPromptBody } from './InteractionPromptBody';

interface CredentialRequestsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** All pending credential requests across the conversation tree. */
  pendingInteractions: Interaction[];
  /** Child conversations (for labeling each request's subagent). */
  childConversations?: Conversation[];
  /**
   * The parent/anchor conversation object — lets a PARENT-owned request viewed
   * from inside a subagent resolve the parent's topic label instead of the
   * generic "Agent <last8>" fallback.
   */
  anchorConversation?: Conversation;
  /** The active (parent) conversation id — labeled "This conversation". */
  activeConversationId?: string;
  /** The request currently opened as a form (null = show the roster). */
  selectedRequestKey: string | null;
  onSelectRequest: (key: string | null) => void;
  onSubmit: (
    conversationId: string,
    requestId: string,
    inputs: Record<string, string>
  ) => Promise<void>;
}

/**
 * B+C-hybrid surface for ENG-3976: a persistent, re-findable right-side drawer
 * that lists every pending subagent credential request (the roster) and, on
 * selecting one, renders its {@link InteractionPromptBody} form in place.
 *
 * Deliberately a DEDICATED surface (not the TraceDrawer tab shell) so the form
 * mode never leaves stray, clickable tabs live above it (uiux-critic fix). It is
 * NON-MODAL — the chat stays interactive — so it declares `aria-modal={false}`
 * and carries no `alertdialog` role. Open/selection state is owned by
 * AIAssistant and prop-drilled (no new Zustand store).
 */
export function CredentialRequestsDrawer({
  open,
  onClose,
  pendingInteractions,
  childConversations = [],
  anchorConversation,
  activeConversationId,
  selectedRequestKey,
  onSelectRequest,
  onSubmit,
}: CredentialRequestsDrawerProps) {
  const labelFor = (
    conversationId: string
  ): { name: string; target?: string } => {
    if (conversationId === activeConversationId) {
      return { name: 'This conversation' };
    }
    const conv =
      childConversations.find(c => (c.uuid || c.id) === conversationId) ??
      // Fall back to the parent/anchor object so a parent-owned request viewed
      // from inside a subagent resolves its real topic, not the uuid fallback.
      (anchorConversation &&
      (anchorConversation.uuid || anchorConversation.id) === conversationId
        ? anchorConversation
        : undefined);
    const { agentLabel, target } = parseSubagentTopic(
      conv?.topic,
      conversationId
    );
    return { name: agentLabel, target };
  };

  const selected =
    selectedRequestKey != null
      ? pendingInteractions.find(i => i.key === selectedRequestKey)
      : undefined;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal={false}
          aria-label="Subagent credential requests"
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
          className="bg-chariot-background-primary border-chariot-stroke absolute top-0 right-0 z-30 flex h-full w-[420px] max-w-[90vw] flex-col border-l shadow-lg"
        >
          {/* Header */}
          <div className="border-chariot-stroke flex items-center justify-between gap-2 border-b px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <div className="bg-chariot-brand-primary/10 text-chariot-brand-primary flex size-7 shrink-0 items-center justify-center rounded-full">
                <KeyIcon className="size-4" />
              </div>
              <span className="text-chariot-text-primary truncate text-sm font-semibold">
                Credential requests
              </span>
              {pendingInteractions.length > 0 && (
                <span className="bg-chariot-status-warning/15 text-chariot-status-warning inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums">
                  {pendingInteractions.length}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close credential requests"
              className="text-chariot-text-secondary hover:text-chariot-text-primary rounded p-1 transition-colors"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>

          {selected ? (
            <>
              {/* Form mode */}
              <button
                type="button"
                onClick={() => onSelectRequest(null)}
                className="text-chariot-brand-primary hover:bg-chariot-background-secondary border-chariot-stroke flex w-full items-center gap-1 border-b px-4 py-2 text-xs"
              >
                <ChevronLeftIcon className="size-3" />
                Back to requests
              </button>
              <div className="border-chariot-stroke flex items-center gap-2 border-b px-4 py-3">
                <div className="min-w-0">
                  <div className="text-chariot-text-primary text-sm font-semibold">
                    {labelFor(selected.conversationId).name} needs credentials
                  </div>
                  {labelFor(selected.conversationId).target && (
                    <div className="text-chariot-text-secondary truncate font-mono text-xs">
                      {labelFor(selected.conversationId).target}
                    </div>
                  )}
                </div>
              </div>
              <InteractionPromptBody
                key={selected.requestId}
                interaction={selected}
                onSubmit={onSubmit}
              />
            </>
          ) : pendingInteractions.length === 0 ? (
            <div className="text-chariot-text-secondary flex flex-1 items-center justify-center p-6 text-center text-sm">
              No pending credential requests.
            </div>
          ) : (
            <div
              className="flex flex-1 flex-col gap-2 overflow-y-auto p-3"
              role="list"
            >
              {pendingInteractions.map(interaction => {
                const { name, target } = labelFor(interaction.conversationId);
                return (
                  <div role="listitem" key={interaction.key}>
                    <button
                      type="button"
                      onClick={() => onSelectRequest(interaction.key)}
                      aria-label={`${name} needs input — open credential request`}
                      className="border-chariot-stroke bg-chariot-background-primary hover:bg-chariot-background-secondary group focus-visible:outline-chariot-brand-primary flex w-full flex-col gap-0.5 rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                      <div className="flex items-center gap-2">
                        <span className="relative flex size-2 shrink-0">
                          <span className="bg-chariot-status-warning absolute inline-flex size-full rounded-full opacity-75 motion-safe:animate-ping" />
                          <span className="bg-chariot-status-warning relative inline-flex size-2 rounded-full" />
                        </span>
                        <span className="text-chariot-text-primary flex-1 truncate text-[13px] font-semibold">
                          {name}
                        </span>
                        <span className="bg-chariot-status-warning/15 text-chariot-status-warning rounded-full px-1.5 text-[10px] font-medium">
                          Needs input
                        </span>
                        <ChevronRightIcon className="text-chariot-text-secondary group-hover:text-chariot-text-primary size-4 shrink-0 transition-colors" />
                      </div>
                      <span className="text-chariot-text-secondary truncate text-[11px]">
                        Credential request
                        {target ? ` · ${target}` : ''}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CredentialRequestsDrawer;
